import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import CountRenderer from '@/app/common/components/CellRenderers/CountRenderer';
import SearchVolumeChart from '@/app/common/components/CellRenderers/SearchVolumeChart';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import { getColor, isFirstRow, isLastRow } from '@/helpers/utils';
import { useState, type ReactNode } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { AiOutlineZoomIn } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { trackerTooltips } from '@/app/KeywordTracker/tooltip/trackerTooltips';
import { commonTooltips } from '@/app/common/tooltip/commonTooltips';
import { showDrawer } from '@/app/common/reducers/drawer';
import AiLogo from '@/assets/img/artificial-intelligence.svg?react';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import Divider from '@/components/common/Divider';
import { intentMap, purposeMap } from './keywordGapColumnConfig';
import SubtleBadge from '@/components/common/SubtleBadge';
import { difficulyRanges, serpDifficuly } from '@/helpers/serpDifficulity';
import type { SearchVolumeKeywordRow } from '@/app/KeywordResearch/types/googleAdsApiTypes';

type PlannerDetailsRow = SearchVolumeKeywordRow & {
  ai_generated?: boolean;
  search_volume?: number;
  competition_index?: number;
  location_code?: string;
  language_code?: string;
};

type PlannerDetailsColumn = ColDef<PlannerDetailsRow> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  gp?: string;
  tooltip?: ReactNode;
  noFilter?: boolean;
  sortable?: boolean;
};

type PlannerTaskGeo = {
  location_code?: string | null;
  language_code?: string | null;
};

const ALL_GEO = 'all';

const pickGeoCode = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (value && value !== ALL_GEO) return value;
  }
  return null;
};

/** /keyword-research/planner/:domain_slug/:domain/:taskId/:loc/:lang/... */
const getPlannerPathParams = (pathname: string) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'keyword-research') {
    return { domain_slug: undefined, loc: undefined, lang: undefined };
  }
  const domain_slug = parts[2];
  if (parts[1] !== 'planner') {
    return { domain_slug, loc: undefined, lang: undefined };
  }
  return { domain_slug, loc: parts[5], lang: parts[6] };
};

export const plannerDetailsColumnConfig = (
  pathname: string,
  taskGeo?: PlannerTaskGeo
): PlannerDetailsColumn[] => {
  const columns: PlannerDetailsColumn[] = [
    {
      field: 'keyword_order',
      type: 'number',
      title: 'ترتیب',
      width: 80,
      searchable: true,
      tooltip:
        'ترتیب ارسال کلمات براساس ورودی (ممکنه برخی کلمات براساس سیاست های گوگل حذف یا تغییر کنن)'
    },
    {
      field: 'keyword',
      type: 'string',
      title: 'کلمه',
      flex: 1,
      minWidth: 250,
      searchable: true,
      tooltip: pathname.includes('planner')
        ? 'لیست کلمات مشابه پرسرچ در اون حوزه رو میتونید مشاهده کنید، منبع این کلمات از گوگل ادز هست.'
        : pathname.includes('ai-keywords')
          ? 'کلمات پیشنهادی توسط هوش مصنوعی تولید شده (AI-Generated Content)'
          : 'لیست کلمات وارد شده توسط شما هست، در نظر داشته باشید که برخی کلمات ممکنه از سمت گوگل حذف بشه.',
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        return (
          <div className="d-flex gap-1 align-items-baseline">
            {decodeURIComponent(params.value)}
            {pathname.includes('ai-keywords') && params.data.ai_generated && (
              <ChildrensTooltip
                placement="top"
                html={
                  <>
                    مقادیر برخی ستون‌ها برای این ردیف توسط هوش مصنوعی تولید شده
                    (AI-Generated Content) مثل سختی و میزان جستجوی کلمات.
                  </>
                }
              >
                <AiLogo
                  style={{
                    width: 13,
                    fill: getColor('gray-600')
                  }}
                />
              </ChildrensTooltip>
            )}
          </div>
        );
      }
    },
    {
      field: 'length',
      type: 'number',
      title: 'طول',
      width: 85,
      searchable: true
    },
    {
      gp: 'جستجو',
      field: 'search_volume_miztols',
      type: 'number',
      title: 'میزان جستجو',
      width: 130,
      searchable: true,
      tooltip: pathname.includes('ai-keywords')
        ? 'میزان تعداد جستجو ماهانه این کلمه که توسط ابزار میزفا تولز یا AI محاسبه شده و اگر AI حساب کرده باشه جلوی کلمه آیکون AI درج شده.'
        : 'میزان تعداد جستجو ماهانه این کلمه که توسط ابزار میزفا تولز محاسبه شده ( روی علامت ذره بین بزنید میتونید سابقه ۴ سال رو مشاهده کنید).',
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        const [opened, setOpened] = useState(false);
        const dispatch = useDispatch();
        const { domain_slug: paramSlug, lang: langParam, loc: locParam } =
          useParams();
        const pathParams = getPlannerPathParams(pathname);
        return (
          <>
            {!params.value ? (
              <HelpTooltip html={commonTooltips.searchVolume} />
            ) : (
              <div className="d-flex align-items-center gap-1">
                {numberHumanizer(params.value)}
                <span
                  style={{
                    display: pathname.includes('ai-keywords') ? 'none' : 'unset'
                  }}
                  onClick={() => {
                    setOpened(true);
                    setTimeout(() => {
                      setOpened(false);
                    }, 1500);
                  }}
                >
                  {!opened ? (
                    <AiOutlineZoomIn
                      onClick={() => {
                        dispatch(
                          showDrawer({
                            key: 'search_volume',
                            payload: {
                              location_code: pickGeoCode(
                                pathParams.loc,
                                locParam,
                                taskGeo?.location_code,
                                params.data.location_code
                              ),
                              language_code: pickGeoCode(
                                pathParams.lang,
                                langParam,
                                taskGeo?.language_code,
                                params.data.language_code
                              ),
                              domain_slug:
                                pathParams.domain_slug || paramSlug || null,
                              keyword: params.data.keyword
                            }
                          })
                        );
                      }}
                      icon="search-plus"
                      className="cursor-pointer fs-0"
                    />
                  ) : (
                    <Spinner
                      animation="border"
                      style={{
                        width: '18px',
                        marginBottom: '-3px',
                        paddingLeft: '3px',
                        height: '18px'
                      }}
                    />
                  )}
                </span>
              </div>
            )}
          </>
        );
      }
    },
    {
      gp: 'جستجو',
      field: 'search_volume_dates',
      type: 'array',
      title: 'روند جستجو',
      tooltip: 'روند جستجوی کلمه در یک سال رو در قالب نمودار بهت نمایش میده.',
      width: 120,
      cellStyle: {
        textAlign: 'center',
        margin: '0px'
      },
      cellClass: 'over-flow',
      noFilter: true,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        return params.value ? (
          <SearchVolumeChart
            isFirstRow={isFirstRow(params)}
            isLastRow={isLastRow(params)}
            count={22}
            dates={params.value}
            series={[
              {
                name: 'روند جستجو',
                data: params.data.search_volume_values.map((value, i) => ({
                  name: params.value[i],
                  value: value,
                  itemStyle: {
                    color: getColor('navbar-vertical-default-link-active-color')
                  }
                }))
              }
            ]}
          />
        ) : (
          ''
        );
      }
    },
    {
      field: 'difficulty',
      gp: 'سنجه‌های کلمه',
      suppressHeaderMenuButton: true,
      type: 'number',
      title: 'سختی ',
      tooltip:
        'هرچه مقدار بالاتر باشد، کسب رتبه در بین ۱۰ نتیجه اول گوگل دشوارتر خواهد بود. سختی بیشتر یعنی نیاز به زمان، هزینه و تکنیک‌های پیشرفته‌تر برای رسیدن به رتبه‌های برتر.',
      width: 90,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) =>
        params.value && (
          <div className="d-flex align-items-center justify-content-center">
            <ChildrensTooltip
              placement="top"
              html={
                <div>
                  سختی کلمه کلیدی
                  <Divider />
                  <Table borderless size="sm">
                    <tbody>
                      {difficulyRanges.map(([start, end]) => (
                        <tr
                          className={classNames({
                            'fw-black fs--1 position-relative':
                              serpDifficuly(start).color ===
                              serpDifficuly(params.value)?.color
                          })}
                          key={start}
                        >
                          <td className="d-flex  align-items-center gap-1">
                            {serpDifficuly(start).color ===
                              serpDifficuly(params.value)?.color && (
                              <FontAwesomeIcon
                                style={{
                                  right: 3,
                                  top: '50%',
                                  transform: 'translateY(-50%)'
                                }}
                                icon="chevron-left"
                                className="position-absolute text-primary"
                                size="sm"
                              />
                            )}
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                direction: 'ltr',
                                height: '20px',
                                width: '28px',
                                fontSize: '12px',
                                backgroundColor: serpDifficuly(start).color
                              }}
                              className="fs--2 px-2 py-0.5 text-white rounded-2"
                            >
                              {start}
                            </span>
                            <FontAwesomeIcon icon="minus" />
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                direction: 'ltr',
                                height: '20px',
                                width: '28px',
                                fontSize: '12px',
                                backgroundColor: serpDifficuly(start).color
                              }}
                              className="fs--2 px-2 py-0.5 text-white rounded-2"
                            >
                              {end}
                            </span>
                          </td>
                          <td className="text-white">
                            {serpDifficuly(start).label}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              }
            >
              <div
                className="fs-1 rounded-2 w-full"
                style={{
                  fontWeight: 'bold'
                }}
              >
                {/* {serpDifficuly(params.value).label}{' '} */}
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    direction: 'ltr',
                    marginTop: '6px',
                    height: '20px',
                    width: '28px',
                    fontSize: '12px',
                    backgroundColor: serpDifficuly(params.value).color
                  }}
                  className="fs--2 px-2 py-0.5 text-white rounded-2"
                >
                  {params.value}
                </span>
              </div>
            </ChildrensTooltip>
          </div>
        )
    },
    {
      field: 'intent',
      gp: 'سنجه‌های کلمه',
      suppressHeaderMenuButton: true,
      type: 'select',
      icon: (
        <AiLogo
          style={{
            width: 13,
            marginTop: 6,
            fill: 'var(--ag-header-foreground-color)'
          }}
        />
      ),
      title: 'نیت',
      width: 90,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        return (
          <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
            {params.value.map((intent, i) => (
              <ChildrensTooltip
                placement="top"
                key={i}
                delayToRemove={0}
                html={intentMap[intent]?.tooltip}
              >
                <SubtleBadge
                  pill
                  key={i}
                  bg={intentMap[intent]?.variant}
                  className="d-flex align-items-center gap-1  fs--2"
                >
                  {intent}
                </SubtleBadge>
              </ChildrensTooltip>
            ))}
          </div>
        );
      },
      tooltip: (
        <div className="text-start">
          نیت جستجو (Search Intent) یعنی قصد کاربر از جستجوی یک کلمه کلیدی چیه؟
          چهار نوع اصلی داره:
          <br /> Informational (هدف اطلاعاتی): کاربر به دنبال اطلاعات درباره یک
          موضوع خاص است. مثال: “چگونه یک ماشین دنده اتومات تعمیر کنیم؟” “فواید
          ورزش برای سلامتی.” این کلمات برای نوشتن مقالات مناسبن. <br />
          Navigational (هدف هدایت‌شده): کاربر به دنبال دسترسی به یک وب‌سایت یا
          موضوع خاص است. مثال: خرید عینک آفتابی در دیجیکالا.
          <br />
          Commercial (تحقیق تجاری): این نوع جستجو ترکیبیه و کاربر هنوز تصمیم
          نگرفته، اما در حال بررسی و مقایسه گزینه‌هاست. مثال: “بهترین گوشی‌های
          2025.” “مقایسه پژو 206 و تیبا.” <br />
          Transactional (هدف تراکنشی یا تجاری): کاربر قصد خرید یا انجام یک اقدام
          خاص را دارد. مثال: “خرید لپ‌تاپ اپل.” “رزرو هتل در کیش.” داده های ستون
          نیت از هوش مصنوعی گرفته میشه.
        </div>
      )
    },
    {
      field: 'purpose',
      gp: 'سنجه‌های کلمه',
      suppressHeaderMenuButton: true,
      type: 'select',
      width: 90,
      title: 'هدف',
      icon: (
        <AiLogo
          style={{
            width: 13,
            marginTop: 6,
            fill: 'var(--ag-header-foreground-color)'
          }}
        />
      ),
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        return (
          <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
            {params.value
              .filter(purpose => purposeMap[purpose])
              .map((purpose, i) => (
                <SubtleBadge
                  pill
                  key={i}
                  bg="info"
                  className="d-flex align-items-center gap-1  fs--2"
                >
                  {purposeMap[purpose]}
                </SubtleBadge>
              ))}
          </div>
        );
      },
      tooltip: (
        <>
          پیشنهاد میده که کلمه برای چه نوع محتوایی مناسب‌تره. مثلاً برای
          <strong>&nbsp;دسته‌بندی </strong>سایت، <strong>&nbsp;مقاله</strong>،
          یا
          <strong>&nbsp;برند</strong> مفیده. با استفاده از این فیلتر می‌تونید
          کلمات کلیدی رو بر اساس هدف خاص خودتون (مثل ایجاد محتوای آموزشی،
          تبلیغاتی یا برندینگ) انتخاب کنید. داده های این بخش از هوش مصنوعی گرفته
          میشه.
        </>
      )
    },

    {
      gp: 'Google Ads',
      tooltip: trackerTooltips.mainGrid.columns.ads_avg_search_volume,
      field: 'search_volume',
      type: 'number',
      title: 'Search Volume',
      width: 140,
      cellStyle: { textAlign: 'center', margin: '0px' },
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        return (
          <>
            {!params.value ? (
              <HelpTooltip html={commonTooltips.searchVolume} />
            ) : (
              <div className="d-flex align-items-center gap-1">
                {numberHumanizer(params.value)}
              </div>
            )}
          </>
        );
      }
    },

    {
      gp: 'Google Ads',
      field: 'competition_index',
      tooltip:
        'رقابتی بودن هر کلمه رو از ۰ تا ۱۰۰ در گوگل ادز نشون میده و هر چی به ۱۰۰ نزدیک تر باشه یعنی در گوگل ادز رقابتی تر هست، پس در سئو هم رقابتی هست ولی برعکس این مورد صدق نمیکنه.',
      type: 'number',
      title: 'Competition',
      width: 140,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        return (
          <>
            {!params.value ? (
              <HelpTooltip html={commonTooltips.searchVolume} />
            ) : (
              <div className="d-flex align-items-center gap-1">
                {numberHumanizer(params.value)}
              </div>
            )}
          </>
        );
      }
    },
    {
      gp: 'رتبه یک در CTR',
      field: 'ctr_rank_one_on_mobile',
      tooltip:
        'CTR یا همون تعداد کلیکی که بعد از گرفتن رتبه یک در این کلمه و در حالت گوشی بدست میاری بهت نمایش میده (برای جزئیات بیشتر حتما راهنمای صفحه رو مطالعه کن، مخصوصا نکته‌های نوشته شده)',
      type: 'number',
      title: 'گوشی',
      width: 100,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        const dispatch = useDispatch();
        return (
          <CountRenderer
            onClick={() => {
              dispatch(
                showDrawer({
                  key: 'ctr',
                  payload: {
                    mobile: params.data.ctr_mobile,
                    desktop: params.data.ctr_desktop,
                    keyword: params.data.keyword
                  }
                })
              );
            }}
            data={params.value}
          />
        );
      }
    },
    {
      gp: 'رتبه یک در CTR',
      field: 'ctr_rank_one_on_desktop',
      type: 'number',
      title: 'رایانه',
      tooltip:
        'CTR یا همون تعداد کلیکی که بعد از گرفتن رتبه یک در این کلمه و در حالت دسکتاپ (رایانه) بدست میاری بهت نمایش میده (برای جزئیات بیشتر حتما راهنمای صفحه رو مطالعه کن، مخصوصا نکته‌های نوشته شده)',
      width: 100,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => {
        const dispatch = useDispatch();
        return (
          <CountRenderer
            onClick={() => {
              dispatch(
                showDrawer({
                  key: 'ctr',
                  payload: {
                    mobile: params.data.ctr_mobile,
                    desktop: params.data.ctr_desktop,
                    keyword: params.data.keyword
                  }
                })
              );
            }}
            data={params.value}
          />
        );
      }
    },
    {
      gp: 'جستجو نسبت به',
      field: 'comparison_sv_last_two_mon',
      type: 'number',
      title: 'ماه قبل',
      tooltip:
        'این ستون میزان جستجو کلمه رو با ماه قبل مقایسه میکنه و متوجه میشی میزان جستجو به نسبت ماه قبل چقدر رشد یا افت داشته.',
      width: 100,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => (
        <div className="d-flex h-100 align-items-center justify-content-around">
          <h6
            className={classNames('mb-0', {
              'text-danger': params.value < 0,
              'text-success': params.value > 0
            })}
          >
            {params.value < 0 ? -1 * params.value : params.value}
            {params.value > 0 ? (
              <FontAwesomeIcon icon="caret-up" className="ms-1" />
            ) : params.value < 0 ? (
              <FontAwesomeIcon icon="caret-down" className="ms-1" />
            ) : (
              ''
            )}
          </h6>
        </div>
      )
    },
    {
      gp: 'جستجو نسبت به',
      field: 'comparison_sv_two_similar_mon',
      type: 'number',
      title: 'سال قبل',
      tooltip:
        'این بخش میزان جستجو کلمه رو با سال قبل در همین تاریخ مقایسه میکنه.',
      width: 100,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PlannerDetailsRow>) => (
        <div className="d-flex h-100 align-items-center justify-content-around">
          <h6
            className={classNames('mb-0', {
              'text-danger': params.value < 0,
              'text-success': params.value > 0
            })}
          >
            {params.value < 0 ? -1 * params.value : params.value}
            {params.value > 0 ? (
              <FontAwesomeIcon icon="caret-up" className="ms-1" />
            ) : params.value < 0 ? (
              <FontAwesomeIcon icon="caret-down" className="ms-1" />
            ) : (
              ''
            )}
          </h6>
        </div>
      )
    },
    {
      gp: 'هزینه به ازای هر کلیک (تومان)',
      field: 'min_cost_per_click',
      type: 'number',
      title: 'حداقل',
      width: 100,
      searchable: true
    },
    {
      gp: 'هزینه به ازای هر کلیک (تومان)',
      field: 'max_cost_per_click',
      type: 'number',
      title: 'حداکثر',
      width: 100,
      searchable: true
    },
  ];
  let filteredColumns: PlannerDetailsColumn[] = columns;

  if (pathname.includes('planner')) {
    filteredColumns = filteredColumns.filter(
      col => col.field !== 'keyword_order'
    );
  }

  if (pathname.includes('ai-keywords')) {
    filteredColumns = filteredColumns.filter(
      col => col.gp !== 'Google Ads' && col.field !== 'keyword_order'
    );
  }

  return filteredColumns;
};
